/**
 * Mask Comparison Section
 * Handles custom product selection dropdowns for the 3-column comparison grid
 * Includes Splide slider for mobile view
 */

class MaskComparison {
  constructor(container) {
    this.container = container;
    this.selectors = container.querySelectorAll('.mask-comparison__selector');
    this.columns = container.querySelectorAll('.mask-comparison__column');
    this.slider = container.querySelector('.mask-comparison__products');
    this.splideInstance = null;
    this.mobileBreakpoint = 991;
    
    this.init();
    this.initSlider();
    this.handleResize();
  }

  init() {
    this.selectors.forEach((selector, columnIndex) => {
      const button = selector.querySelector('.mask-comparison__selector-button');
      const dropdown = selector.querySelector('.mask-comparison__selector-dropdown');
      const options = dropdown.querySelectorAll('.mask-comparison__selector-option');
      const closeBtn = dropdown.querySelector('.mask-comparison__dropdown-close');

      // Store reference to dropdown on selector for later access
      selector._dropdown = dropdown;

      // Close button click
      if (closeBtn) {
        closeBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.closeAllDropdowns();
          button.focus();
        });
      }

      // Toggle dropdown on button click
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        const wasOpen = selector.classList.contains('is-open');
        this.closeAllDropdowns();
        
        if (!wasOpen) {
          selector.classList.add('is-open');
          button.setAttribute('aria-expanded', 'true');
          this.openDropdown(selector, dropdown);
          // Focus the selected option, or first option if none selected
          const selectedOption = dropdown.querySelector('.mask-comparison__selector-option.is-selected') 
            || dropdown.querySelector('.mask-comparison__selector-option');
          if (selectedOption) selectedOption.focus();
        }
      });

      // Keyboard navigation on button
      button.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (!selector.classList.contains('is-open')) {
            selector.classList.add('is-open');
            button.setAttribute('aria-expanded', 'true');
            const firstOption = dropdown.querySelector('.mask-comparison__selector-option');
            if (firstOption) firstOption.focus();
          }
        }
      });

      // Handle option selection and keyboard navigation
      options.forEach((option, optionIndex) => {
        option.addEventListener('click', (e) => {
          e.stopPropagation();
          this.selectOption(selector, option, columnIndex, button);
        });

        option.addEventListener('keydown', (e) => {
          switch (e.key) {
            case 'Enter':
            case ' ':
              e.preventDefault();
              this.selectOption(selector, option, columnIndex, button);
              break;
            case 'ArrowDown':
              e.preventDefault();
              if (optionIndex < options.length - 1) {
                options[optionIndex + 1].focus();
              }
              break;
            case 'ArrowUp':
              e.preventDefault();
              if (optionIndex > 0) {
                options[optionIndex - 1].focus();
              } else {
                button.focus();
              }
              break;
            case 'Escape':
              e.preventDefault();
              this.closeAllDropdowns();
              button.focus();
              break;
            case 'Tab':
              this.closeAllDropdowns();
              break;
          }
        });
      });
    });

    // Close dropdowns when clicking outside
    document.addEventListener('click', () => {
      this.closeAllDropdowns();
    });

    // Close dropdowns on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeAllDropdowns();
      }
    });
  }

  selectOption(selector, option, columnIndex, button) {
    const productId = option.dataset.productId;
    const productTitle = option.dataset.productTitle;
    const productImage = option.dataset.productImage;
    
    // Get the dropdown from stored reference
    const dropdown = selector._dropdown;

    // Update button display
    this.updateButtonDisplay(selector, productTitle, productImage);

    // Clear selected state from all options in this dropdown
    if (dropdown) {
      dropdown.querySelectorAll('.mask-comparison__selector-option').forEach(opt => {
        opt.classList.remove('is-selected');
        opt.setAttribute('aria-selected', 'false');
      });
    }
    
    // Mark the clicked option as selected
    option.classList.add('is-selected');
    option.setAttribute('aria-selected', 'true');

    // Show the selected product
    this.handleProductChange(productId, columnIndex);

    // Close dropdown properly (handles modal state and backdrop)
    selector.classList.remove('is-open');
    button.setAttribute('aria-expanded', 'false');
    this.closeDropdown(dropdown);
    button.focus();
  }

  updateButtonDisplay(selector, title, imageUrl) {
    const titleEl = selector.querySelector('.mask-comparison__selector-title');
    const imageEl = selector.querySelector('.mask-comparison__selector-image img');

    if (titleEl) titleEl.textContent = title;
    if (imageEl && imageUrl) imageEl.src = imageUrl;
  }

  closeAllDropdowns() {
    this.selectors.forEach(selector => {
      selector.classList.remove('is-open');
      const button = selector.querySelector('.mask-comparison__selector-button');
      if (button) button.setAttribute('aria-expanded', 'false');
      
      // Use stored dropdown reference
      const dropdown = selector._dropdown;
      if (dropdown) this.closeDropdown(dropdown);
    });
  }

  openDropdown(selector, dropdown) {
    const isMobile = window.innerWidth < this.mobileBreakpoint;
    
    if (isMobile) {
      // Move dropdown to body for proper fixed positioning
      dropdown._originalParent = dropdown.parentNode;
      document.body.appendChild(dropdown);
      dropdown.classList.add('is-modal');
      
      // Show backdrop
      this.showBackdrop();
    }
  }

  closeDropdown(dropdown) {
    if (dropdown && dropdown._originalParent) {
      dropdown._originalParent.appendChild(dropdown);
      dropdown.classList.remove('is-modal');
      delete dropdown._originalParent;
    }
    
    // Hide backdrop
    this.hideBackdrop();
  }

  showBackdrop() {
    if (this.backdrop) return;
    
    this.backdrop = document.createElement('div');
    this.backdrop.className = 'mask-comparison__backdrop';
    this.backdrop.addEventListener('click', () => this.closeAllDropdowns());
    document.body.appendChild(this.backdrop);
    
    // Trigger reflow for animation
    this.backdrop.offsetHeight;
    this.backdrop.classList.add('is-visible');
  }

  hideBackdrop() {
    if (!this.backdrop) return;
    
    this.backdrop.classList.remove('is-visible');
    setTimeout(() => {
      if (this.backdrop && this.backdrop.parentNode) {
        this.backdrop.parentNode.removeChild(this.backdrop);
      }
      this.backdrop = null;
    }, 200);
  }

  handleProductChange(selectedProductId, columnIndex) {
    const column = this.columns[columnIndex];
    const products = column.querySelectorAll('.mask-comparison__product');

    // Hide all products in this column
    products.forEach(product => {
      product.classList.remove('is-active');
    });

    // Show the selected product
    const selectedProduct = column.querySelector(`.mask-comparison__product[data-product-id="${selectedProductId}"]`);
    if (selectedProduct) {
      selectedProduct.classList.add('is-active');
    }
  }

  initSlider() {
    if (!this.slider || typeof Splide === 'undefined') return;

    // Only initialize on mobile
    if (window.innerWidth < this.mobileBreakpoint) {
      this.mountSlider();
    }
  }

  mountSlider() {
    if (this.splideInstance) return;

    this.splideInstance = new Splide(this.slider, {
      type: 'slide',
      perPage: 2,
      perMove: 1,
      gap: '0px',
      padding: { right: '0%' },
      arrows: false,
      pagination: false,
      mediaQuery: 'min',
      breakpoints: {
        1100: {
          destroy: true,
          padding: { right: '40%' }
        },
        768: {
          padding: { right: '18%' },
          gap: '10px'
        }
      }
    });

    this.splideInstance.mount();

    // Setup scrollbar using SplideUtils
    if (typeof window.SplideUtils !== 'undefined' && window.SplideUtils.setupScrollbar) {
      window.SplideUtils.setupScrollbar(this.splideInstance, this.container);
    }
  }

  destroySlider() {
    if (this.splideInstance) {
      this.splideInstance.destroy();
      this.splideInstance = null;

      // Clean up inline styles that Splide adds
      const track = this.slider.querySelector('.splide__track');
      const list = this.slider.querySelector('.splide__list');
      const slides = this.slider.querySelectorAll('.splide__slide');

      if (track) track.removeAttribute('style');
      if (list) list.removeAttribute('style');
      slides.forEach(slide => slide.removeAttribute('style'));

      // Remove Splide classes that might interfere
      this.slider.classList.remove('is-initialized', 'is-active');
    }
  }

  handleResize() {
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (window.innerWidth < this.mobileBreakpoint) {
          if (!this.splideInstance) {
            this.mountSlider();
          }
        } else {
          this.destroySlider();
        }
      }, 250);
    });
  }

  destroy() {
    this.destroySlider();
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', function() {
  const sections = document.querySelectorAll('[data-mask-comparison]');
  sections.forEach(section => {
    section.maskComparisonInstance = new MaskComparison(section);
  });
});

// Shopify Theme Editor support
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(event) {
    const section = event.target.querySelector('[data-mask-comparison]');
    if (section) {
      section.maskComparisonInstance = new MaskComparison(section);
    }
  });

  document.addEventListener('shopify:section:unload', function(event) {
    const section = event.target.querySelector('[data-mask-comparison]');
    if (section && section.maskComparisonInstance) {
      section.maskComparisonInstance.destroy();
    }
  });
}
